import React from 'react';
import {
  Heading,
  List,
  Toast,
  ToastType,
} from '@alefeducation/ui-elements-library-core';
import styled from 'styled-components';
import { useAddMember } from './useMember';
import {
  CheckEmail,
  Footer,
  GeneralInformation,
} from '../../components/AddEditMember';
import { containerSpace } from '../../components/AddEditMember/styled';
import { withBreadcrumbs } from '../../components/Routing/Breadcrumbs';
import Roles from '../../components/Roles/Roles';
import {
  ADD_MEMBER,
  FILL_FORM_TO_ADD_MEMBER,
  MEMBER_SUCCESSFULLY_ADDED,
} from '../../utils/constants';

const StyledListContainer = styled(List)`
  display: flex;
  flex-direction: column;
  height: auto;
`;

const StyledTableHeader = styled.div`
  ${containerSpace};
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }: any) => `${theme.space[8]}px`};
  border-bottom: 1px solid ${({ theme }: any) => theme.colors.primary[10]};
`;

const StyledBody = styled.div`
  overflow-x: auto;
`;

export enum UserType {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
  ORG_MEMBER = 'ORG_MEMBER',
  ALEF_ONLY = 'ALEF_ONLY',
}

interface Props {
  isOrgMember?: boolean;
}

const AddMember: React.FC<Props> = ({ isOrgMember = false }) => {
  const {
    userType,
    disabled,
    hasUpdatePermission,
    userQuery,
    form,
    toast,
    pageNumber,
    organisationCode,
    userData,
  } = useAddMember({ isOrgMember });

  const { fetchUserError, loading } = userQuery;

  const {
    selectedRoles,
    email,
    setEmail,
    emailValid,
    password,
    confirmPassword,
    name,
    firstName,
    lastName,
    userId,
    isCheckClicked,
    setIsCheckClicked,
    isFormInValid,
    validateEmail,
    onAddMember,
    onCheckEmail,
    onRoleChange,
    onNameChange,
    onFirstNameChange,
    onLastNameChange,
    onPassChange,
    onConfirmPassChange,
    onCancelClick,
  } = form;

  const { showSuccessToast, setShowSuccessToast } = toast;
  const isSubmitDisabled = isFormInValid();

  const renderRolesUI = () => {
    return (
      <Roles
        disabled={disabled}
        selectedRoles={selectedRoles}
        setSelectedRoles={onRoleChange}
        user={userData}
        isOrgMember={isOrgMember}
      />
    );
  };

  return (
    <StyledListContainer>
      <StyledTableHeader>
        <Heading variant="heading-medium">{ADD_MEMBER}</Heading>
        <Heading variant="heading-small" weight="light" color="primary.60">
          {FILL_FORM_TO_ADD_MEMBER}
        </Heading>
      </StyledTableHeader>
      <StyledBody>
        <CheckEmail
          userType={userType}
          userId={userId}
          disabled={loading}
          email={email}
          onEmailChange={setEmail}
          onCheckEmailClick={onCheckEmail}
          onInputBlur={validateEmail}
          hasError={!!fetchUserError}
          isEmailValid={emailValid}
          isCheckClicked={isCheckClicked}
          setIsCheckClicked={setIsCheckClicked}
          paginationContext={{ orgCode: organisationCode, pageNumber }}
          isOrgMember={isOrgMember}
          hasUpdatePermission={hasUpdatePermission}
        />
        <GeneralInformation
          email={email}
          disabled={disabled}
          name={name}
          firstName={firstName}
          lastName={lastName}
          userType={userType}
          onNameChange={onNameChange}
          onFirstNameChange={onFirstNameChange}
          onLastNameChange={onLastNameChange}
          onPassChange={onPassChange}
          onConfirmPassChange={onConfirmPassChange}
          password={password}
          confirmPass={confirmPassword}
        />
        {renderRolesUI()}
      </StyledBody>
      <Footer
        disableSubmitButton={isSubmitDisabled}
        onAddMember={onAddMember}
        onCancel={onCancelClick}
      />
      <Toast
        data-testid="add-member-success-toast"
        isOpen={showSuccessToast}
        type={ToastType.SUCCESS}
        content={{ title: MEMBER_SUCCESSFULLY_ADDED, description: '' }}
        autoHide={true}
        onHide={() => {
          setShowSuccessToast(false);
        }}
      />
      <div id="add-member-error-toast" />
    </StyledListContainer>
  );
};

export default withBreadcrumbs(AddMember);
